import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { acceptInvitation, previewInvitation, type InvitationPreview } from '../api/invite'
import { ApiError } from '../api/client'
import { errorMessage, formatUtc } from '../lib/format'

/**
 * The page behind the emailed link. Rendered WITHOUT Keycloak (see main.tsx): the
 * person has no account to sign in with yet. They read what they were invited to, set
 * a name and a password, and are sent to the sign-in, where the realm's browser flow
 * enrols their authenticator before the room shows.
 */
export function InviteAccept({ token }: { token: string }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'invalid'; message: string }
    | { kind: 'ready'; preview: InvitationPreview }
    | { kind: 'done'; continueUrl: string; roomName: string }
  >({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    previewInvitation(token)
      .then((preview) => {
        if (!cancelled) setState({ kind: 'ready', preview })
      })
      .catch((e: unknown) => {
        if (!cancelled) setState({ kind: 'invalid', message: errorMessage(e) })
      })
    return () => {
      cancelled = true
    }
  }, [token])

  return (
    <div className="shell">
      <header className="shell-header">
        <span className="wordmark">
          <span className="wordmark-mark" aria-hidden="true" />
          Kurrawa Data Room
        </span>
      </header>
      <main className="shell-main">
        {state.kind === 'loading' && <p className="muted">Checking your invitation…</p>}
        {state.kind === 'invalid' && (
          <div className="alert alert-error" role="alert">
            <p className="alert-title">This invitation cannot be used.</p>
            <p>{state.message}</p>
            <p className="small muted">
              Links expire and can be used once. Ask the person who invited you for a new one.
            </p>
          </div>
        )}
        {state.kind === 'ready' && (
          <AcceptForm
            token={token}
            preview={state.preview}
            onDone={(continueUrl, roomName) => setState({ kind: 'done', continueUrl, roomName })}
          />
        )}
        {state.kind === 'done' && (
          <section className="panel invite-panel">
            <h1 className="h1">Your account is ready</h1>
            <p>
              Sign in with the password you just chose. On first sign-in you will set up an
              authenticator app (or a passkey) before <strong>{state.roomName}</strong> opens.
            </p>
            <a className="btn btn-primary" href={state.continueUrl}>
              Continue to sign in
            </a>
          </section>
        )}
      </main>
      <footer className="shell-footer">Invitation</footer>
    </div>
  )
}

const schema = z
  .object({
    displayName: z.string().trim().min(1, 'Your name is required').max(200),
    password: z.string().min(12, 'Use at least 12 characters').max(256),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'The passwords differ' })
type Form = z.infer<typeof schema>

function AcceptForm({
  token,
  preview,
  onDone,
}: {
  token: string
  preview: InvitationPreview
  onDone: (continueUrl: string, roomName: string) => void
}) {
  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { displayName: '', password: '', confirm: '' },
  })
  const [serverError, setServerError] = useState<{ field?: string; message: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = form.handleSubmit(async (values) => {
    setServerError(null)
    setBusy(true)
    try {
      const result = await acceptInvitation(token, values.displayName, values.password)
      onDone(result.continueUrl, result.roomName)
    } catch (e) {
      if (e instanceof ApiError && e.violations.password) {
        setServerError({ field: 'password', message: e.violations.password })
      } else {
        setServerError({ message: errorMessage(e) })
      }
    } finally {
      setBusy(false)
    }
  })

  return (
    <section className="panel invite-panel">
      <p className="small muted">{preview.organizationName}</p>
      <h1 className="h1">You are invited to {preview.roomName}</h1>
      <p>
        {preview.invitedBy ? `${preview.invitedBy} has` : 'A room owner has'} invited{' '}
        <strong>{preview.email}</strong> to this data room. The invitation is valid until{' '}
        {formatUtc(preview.expiresUtc)}.
      </p>
      {preview.message && <blockquote className="invite-message">{preview.message}</blockquote>}

      <form className="form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span>Your name</span>
          <input {...form.register('displayName')} autoComplete="name" />
          <span className="field-error">{form.formState.errors.displayName?.message}</span>
        </label>
        <label className="field">
          <span>Choose a password</span>
          <input type="password" {...form.register('password')} autoComplete="new-password" />
          <span className="field-error">
            {form.formState.errors.password?.message ??
              (serverError?.field === 'password' ? serverError.message : '')}
          </span>
        </label>
        <label className="field">
          <span>Confirm the password</span>
          <input type="password" {...form.register('confirm')} autoComplete="new-password" />
          <span className="field-error">{form.formState.errors.confirm?.message}</span>
        </label>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Setting up…' : 'Accept and create my account'}
          </button>
          {serverError && !serverError.field && (
            <span className="error-text small">{serverError.message}</span>
          )}
        </div>
        <p className="small muted">
          Signing in will also ask you to set up an authenticator app or a passkey: every account in
          this data room uses two factors.
        </p>
      </form>
    </section>
  )
}
